import { del, get, post, put } from "@/lib/api";

export const GUEST_CART_STORAGE_KEY = "dw.cart.guest.v1";
export const USER_CART_CACHE_KEY = "dw.cart.user-cache.v1";
export const LEGACY_CART_ITEM_KEY = "dw-cart-items";
export const LEGACY_CART_ADDONS_KEY = "dw-cart-addons";
export const LEGACY_CART_MASA_KEY = "dw-cart-masa";
export const LEGACY_CART_DOMAIN_KEY = "dw-cart-domain";

const DEFAULT_ACTIVE_PERIOD = "6";

function nowIso() {
  return new Date().toISOString();
}

function isClient() {
  return typeof window !== "undefined";
}

function readJsonStorage(key, fallback = null) {
  if (!isClient()) {
    return fallback;
  }

  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) {
      return fallback;
    }
    return JSON.parse(raw);
  } catch {
    return fallback;
  }
}

function writeJsonStorage(key, value) {
  if (!isClient()) {
    return;
  }

  if (value === null || value === undefined) {
    window.localStorage.removeItem(key);
    return;
  }

  window.localStorage.setItem(key, JSON.stringify(value));
}

function coerceString(value) {
  return String(value || "").trim();
}

function normalizeTemplate(template) {
  if (!template || typeof template !== "object") {
    return null;
  }

  const id = template.id || template.product_id || template.productId || null;
  const slug = coerceString(template.slug || template.template_slug || template.templateSlug);
  const name = coerceString(template.name || template.template_name || template.templateName);
  const price = Number(template.price || template.discountedPrice || 0);
  const imageUrl = (
    template.image_url ||
    template.image ||
    template.template_image ||
    template.templateImage ||
    null
  );

  if (!id && !slug) {
    return null;
  }

  return {
    id: id || slug,
    slug,
    name: name || "Template Undangan",
    price: Number.isFinite(price) ? price : 0,
    image_url: imageUrl,
  };
}

export function normalizeCartTemplate(template) {
  return normalizeTemplate(template);
}

function normalizeAddons(addons) {
  if (!Array.isArray(addons)) {
    return [];
  }

  const normalized = [];
  const seenKeys = new Set();

  addons.forEach((addon) => {
    const key = coerceString(
      typeof addon === "string" ? addon : addon?.key,
    );
    if (!key || seenKeys.has(key)) {
      return;
    }
    seenKeys.add(key);

    if (typeof addon === "string") {
      normalized.push({ key });
      return;
    }

    const value = addon?.value;
    if (value === undefined || value === null || value === "") {
      normalized.push({ key });
      return;
    }

    normalized.push({
      key,
      value: String(value),
    });
  });

  return normalized;
}

function extractActivePeriod(order) {
  const rawPeriod =
    order?.activePeriod ||
    order?.active_period ||
    order?.masaAktif ||
    order?.masa_aktif;
  const fromOrder = coerceString(rawPeriod);
  if (fromOrder) {
    return fromOrder;
  }

  const addons = normalizeAddons(order?.addons);
  const activePeriodAddon = addons.find((addon) => addon.key === "activePeriod");
  const fromAddon = coerceString(activePeriodAddon?.value);
  if (fromAddon) {
    return fromAddon;
  }

  return DEFAULT_ACTIVE_PERIOD;
}

function withActivePeriodAddon(addons, activePeriod) {
  const normalized = normalizeAddons(addons).filter(
    (addon) => addon.key !== "activePeriod",
  );

  const value = coerceString(activePeriod) || DEFAULT_ACTIVE_PERIOD;
  normalized.push({
    key: "activePeriod",
    value,
  });

  return normalized;
}

function normalizeContact(contact) {
  return {
    email: coerceString(contact?.email),
    phone: coerceString(contact?.phone),
  };
}

function normalizeCoupleDetails(coupleDetails) {
  return {
    bride: coerceString(coupleDetails?.bride),
    groom: coerceString(coupleDetails?.groom),
    weddingDate: coerceString(coupleDetails?.weddingDate),
    notes: coerceString(coupleDetails?.notes),
  };
}

function normalizeOrderDraft(order) {
  const activePeriod = extractActivePeriod(order);
  return {
    requestedPublicSlug: coerceString(
      order?.requestedPublicSlug || order?.requested_public_slug,
    ),
    addons: withActivePeriodAddon(order?.addons, activePeriod),
    customDomain: coerceString(order?.customDomain || order?.custom_domain),
    activePeriod,
  };
}

function normalizeCloudDiy(cloudDiy) {
  if (!cloudDiy || typeof cloudDiy !== "object") {
    return {
      ready: false,
      payload: {},
    };
  }

  return {
    ready: Boolean(cloudDiy.ready),
    payload:
      cloudDiy.payload && typeof cloudDiy.payload === "object"
        ? cloudDiy.payload
        : {},
  };
}

function normalizeMeta(meta, ownerTypeFallback = "GUEST") {
  const ownerType = coerceString(meta?.owner_type || meta?.ownerType).toUpperCase();
  const normalizedOwnerType = ownerType || ownerTypeFallback;
  const updatedAt = coerceString(meta?.updated_at || meta?.updatedAt) || nowIso();
  const revision = Number(meta?.revision || 0);

  return {
    owner_type: normalizedOwnerType,
    updated_at: updatedAt,
    revision: Number.isFinite(revision) ? revision : 0,
  };
}

export function createEmptyCartState(ownerType = "GUEST") {
  return {
    template: null,
    cart: {
      contact: {
        email: "",
        phone: "",
      },
      coupleDetails: {
        bride: "",
        groom: "",
        weddingDate: "",
        notes: "",
      },
      order: {
        requestedPublicSlug: "",
        addons: [{ key: "activePeriod", value: DEFAULT_ACTIVE_PERIOD }],
        customDomain: "",
        activePeriod: DEFAULT_ACTIVE_PERIOD,
      },
      cloudDiy: {
        ready: false,
        payload: {},
      },
    },
    meta: {
      owner_type: ownerType,
      updated_at: nowIso(),
      revision: 0,
    },
  };
}

export function normalizeCartState(rawState, ownerTypeFallback = "GUEST") {
  if (!rawState || typeof rawState !== "object") {
    return createEmptyCartState(ownerTypeFallback);
  }

  const cartNode = rawState.cart && rawState.template
    ? rawState
    : rawState.cart && rawState.cart.template && rawState.cart.cart
      ? rawState.cart
      : rawState;

  const normalizedTemplate = normalizeTemplate(cartNode?.template);
  const cartDraft = cartNode?.cart || {};
  const normalizedOrder = normalizeOrderDraft(cartDraft?.order || {});

  return {
    template: normalizedTemplate,
    cart: {
      contact: normalizeContact(cartDraft?.contact || {}),
      coupleDetails: normalizeCoupleDetails(cartDraft?.coupleDetails || {}),
      order: normalizedOrder,
      cloudDiy: normalizeCloudDiy(cartDraft?.cloudDiy || {}),
    },
    meta: normalizeMeta(cartNode?.meta || {}, ownerTypeFallback),
  };
}

export function bumpCartMeta(state, ownerType) {
  const nextState = normalizeCartState(state, ownerType || "GUEST");
  const nextRevision = Number(nextState?.meta?.revision || 0) + 1;
  return {
    ...nextState,
    meta: {
      owner_type: ownerType || nextState.meta.owner_type || "GUEST",
      updated_at: nowIso(),
      revision: nextRevision,
    },
  };
}

export function extractCartEnvelope(response) {
  const root = response?.data || response || {};
  if (root?.cart && typeof root.cart === "object") {
    return root.cart;
  }
  if (root?.template || root?.cart || root?.meta) {
    return root;
  }
  return null;
}

export function toCartPutPayload(state) {
  const normalized = normalizeCartState(state, "USER");
  const parsedRevision = Number(normalized.meta?.revision);
  const revision = Number.isInteger(parsedRevision) && parsedRevision >= 1 ? parsedRevision : 1;
  return {
    template: normalized.template,
    cart: normalized.cart,
    meta: {
      revision: revision,
    },
  };
}

export function readGuestCartFromStorage() {
  return normalizeCartState(readJsonStorage(GUEST_CART_STORAGE_KEY), "GUEST");
}

export function writeGuestCartToStorage(state) {
  const normalized = normalizeCartState(state, "GUEST");
  writeJsonStorage(GUEST_CART_STORAGE_KEY, normalized);
}

export function clearGuestCartStorage() {
  writeJsonStorage(GUEST_CART_STORAGE_KEY, null);
}

export function readUserCartCache() {
  return normalizeCartState(readJsonStorage(USER_CART_CACHE_KEY), "USER");
}

export function writeUserCartCache(state) {
  const normalized = normalizeCartState(state, "USER");
  writeJsonStorage(USER_CART_CACHE_KEY, normalized);
}

export function clearUserCartCache() {
  writeJsonStorage(USER_CART_CACHE_KEY, null);
}

export function clearLegacyCartStorage() {
  if (!isClient()) {
    return;
  }
  window.localStorage.removeItem(LEGACY_CART_ITEM_KEY);
  window.localStorage.removeItem(LEGACY_CART_ADDONS_KEY);
  window.localStorage.removeItem(LEGACY_CART_MASA_KEY);
  window.localStorage.removeItem(LEGACY_CART_DOMAIN_KEY);
}

export function readLegacyCartAsGuestState() {
  const rawItems = readJsonStorage(LEGACY_CART_ITEM_KEY, []);
  if (!Array.isArray(rawItems) || rawItems.length === 0) {
    return null;
  }

  const firstItem = rawItems[0];
  const template = normalizeTemplate({
    id: firstItem?.id,
    slug: firstItem?.slug,
    name: firstItem?.name,
    price: firstItem?.price,
    image_url:
      firstItem?.image_url ||
      firstItem?.image ||
      firstItem?.thumbnail ||
      firstItem?.thumbnail_url,
  });
  if (!template) {
    return null;
  }

  const rawAddons = readJsonStorage(LEGACY_CART_ADDONS_KEY, {});
  const legacyAddons = Object.entries(rawAddons || {})
    .filter(([, selected]) => Boolean(selected))
    .map(([key]) => ({ key: coerceString(key) }))
    .filter((addon) => addon.key && addon.key !== "activePeriod");
  const activePeriod = coerceString(readJsonStorage(LEGACY_CART_MASA_KEY, "")) || DEFAULT_ACTIVE_PERIOD;
  const customDomain = coerceString(readJsonStorage(LEGACY_CART_DOMAIN_KEY, ""));

  const mergedAddons = withActivePeriodAddon(legacyAddons, activePeriod);
  const base = createEmptyCartState("GUEST");

  return {
    ...base,
    template,
    cart: {
      ...base.cart,
      order: {
        ...base.cart.order,
        addons: mergedAddons,
        activePeriod,
        customDomain,
      },
    },
    meta: {
      owner_type: "GUEST",
      updated_at: nowIso(),
      revision: 1,
    },
  };
}

export function toInvitationItem(template) {
  if (!template) {
    return [];
  }
  return [{
    id: template.id || template.slug || "template",
    slug: coerceString(template.slug),
    name: template.name || "Template Undangan",
    price: Number(template.price || 0),
    image_url: template.image_url || null,
    type: "invitation",
    qty: 1,
  }];
}

export function getSelectedAddonsMap(orderDraft) {
  const map = {};
  const addons = normalizeAddons(orderDraft?.addons || []);
  addons.forEach((addon) => {
    if (!addon?.key || addon.key === "activePeriod") {
      return;
    }
    map[addon.key] = true;
  });
  return map;
}

export function mergeAddonToggle(orderDraft, key, selected) {
  const cleanKey = coerceString(key);
  if (!cleanKey) {
    return normalizeOrderDraft(orderDraft || {});
  }

  const current = normalizeOrderDraft(orderDraft || {});
  const addonsWithoutKey = normalizeAddons(current.addons).filter(
    (addon) => addon.key !== cleanKey,
  );

  const nextAddons = selected
    ? [...addonsWithoutKey, { key: cleanKey }]
    : addonsWithoutKey;

  const merged = normalizeOrderDraft({
    ...current,
    addons: nextAddons,
  });

  if (!selected && cleanKey === "customDomain") {
    merged.customDomain = "";
  }

  return merged;
}

export function setActivePeriod(orderDraft, value) {
  const current = normalizeOrderDraft(orderDraft || {});
  const nextPeriod = coerceString(value) || DEFAULT_ACTIVE_PERIOD;
  return normalizeOrderDraft({
    ...current,
    activePeriod: nextPeriod,
    addons: withActivePeriodAddon(current.addons, nextPeriod),
  });
}

export async function getCloudCart() {
  return get("/cart", {
    useAuth: true,
    useApiKey: true,
  });
}

export async function upsertCloudCart(state) {
  return put("/cart", toCartPutPayload(state), {
    useAuth: true,
    useApiKey: true,
  });
}

export async function clearCloudCart() {
  return del("/cart", {
    useAuth: true,
    useApiKey: true,
  });
}

export async function claimGuestCartToCloud(guestCart, conflictPolicy = "LATEST_WINS") {
  const normalizedGuestCart = normalizeCartState(guestCart, "GUEST");
  return post("/cart/claim-guest", {
    guestCart: {
      template: normalizedGuestCart.template,
      cart: normalizedGuestCart.cart,
      meta: {
        updated_at: normalizedGuestCart.meta.updated_at || nowIso(),
      },
    },
    conflict_policy: conflictPolicy,
  }, {
    useAuth: true,
    useApiKey: true,
  });
}
